import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@circle-react/types";
import { ConnectionCTA } from "../../ProfileModalV3/Body/ConnectBanner/ConnectionCTA";
import { Icon } from "../../shared/Icon";
import { MemberCardImage } from "../../shared/MembersList/CommunityMembers/CardView/MemberCardImage";
import { useDetermineConnectionStatus } from "../../shared/MembersList/CommunityMembers/hooks/useDetermineConnectionStatus";
import { DismissRecommendation } from "./DismissRecommendation";
import type { ExpandableAccordionItem } from "./ExpandableAccordion";

export interface ExpandableListItemProps {
  communityMember: CommunityMember;
  item: ExpandableAccordionItem;
}

export const ExpandableListItem = ({
  communityMember,
  item,
}: ExpandableListItemProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember?.public_uid ?? "",
  });

  const memberConnection =
    communityMember?.connection_to_current_community_member;

  const { isOtherUserSentConnection } =
    useDetermineConnectionStatus(memberConnection);

  if (!communityMember) {
    return null;
  }

  return (
    <li
      className="border-primary bg-primary group flex w-full min-w-0 flex-1 gap-x-4 rounded-2xl px-6 py-4"
      data-testid="expandable-list-item"
    >
      <div style={{ aspectRatio: "1 / 1" }} className="h-10">
        <a {...profileLinkProps}>
          <MemberCardImage
            name={communityMember.name ?? ""}
            src={communityMember.avatar_url ?? ""}
            className="!rounded-full"
          />
        </a>
      </div>

      <div className="flex w-full min-w-0 flex-1 flex-col gap-y-3">
        <div className="flex w-full min-w-0 flex-1 flex-row justify-between gap-y-3">
          <a {...profileLinkProps} className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-col gap-y-3">
              <div className="flex flex-col">
                <Typography.LabelSm
                  truncate
                  color="text-dark"
                  weight="semibold"
                >
                  {communityMember.name}
                </Typography.LabelSm>

                <Typography.BodyXs truncate color="text-default">
                  {communityMember.headline}
                </Typography.BodyXs>
              </div>

              {communityMember.location && (
                <div className="flex items-center gap-1">
                  <Typography.LabelXs truncate color="text-default">
                    <Icon type="16-location" size={16} />
                    {communityMember.location}
                  </Typography.LabelXs>
                </div>
              )}
            </div>
          </a>
          <div className="flex w-fit items-start gap-2">
            <ConnectionCTA member={communityMember} />
            {item.type == "recommendation" && (
              <DismissRecommendation recommendationId={item.id} />
            )}
          </div>
        </div>

        {isOtherUserSentConnection && memberConnection?.note && (
          <ConnectionNote note={memberConnection?.note} />
        )}
      </div>
    </li>
  );
};
